import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Logo from '../common/Logo';
import { hashPassword } from '../../utils/helpers';
import { useAuth } from '../../contexts/AuthContext';
import { motion } from 'framer-motion';
import { GoogleLogin } from '@react-oauth/google';

function Login({ version, googleClientId }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [is2FARequired, setIs2FARequired] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || "/";

  const isGoogleConfigured = googleClientId && 
                             googleClientId !== "YOUR_GOOGLE_CLIENT_ID.apps.googleusercontent.com" && 
                             googleClientId.includes(".apps.googleusercontent.com");

  const handleGoogleSuccess = (credentialResponse) => {
    if (isSubmitting) return;
    setError('');
    setIsSubmitting(true);
    fetch('/login/google', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id_token: credentialResponse.credential }),
      credentials: 'include',
    })
    .then(async res => {
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    })
    .then(data => {
      if (data) {
        login(data);
        navigate(from, { replace: true });
      }
    })
    .catch(err => setError(err.message))
    .finally(() => setIsSubmitting(false));
  };

  const handleGoogleError = () => {
    setError("Google Sign In failed. Please try again.");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setError('');
    setIsSubmitting(true);

    try {
      const hashedPassword = await hashPassword(password);
      const res = await fetch('/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password: hashedPassword, code: twoFactorCode }),
        credentials: 'include',
      });

      if (res.status === 202) {
        setIs2FARequired(true);
        return;
      }
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      if (data) {
        login(data);
        navigate(from, { replace: true });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="login-container">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        className="login-card"
      >
        <div className="logo-container">
          <Logo />
          <h1 style={{fontSize: '1.5rem', fontWeight: '500', margin: 0, color: 'var(--google-grey-700)'}}>Farm Admin</h1>
        </div>
        
        <h2 style={{ marginBottom: '0.5rem', fontSize: '1.5rem', fontWeight: '400' }}>Sign in</h2>
        <p style={{color: 'var(--google-grey-600)', marginBottom: '2.5rem', fontSize: '1rem'}}>Use your Farm Admin account</p>

        {isGoogleConfigured && (
          <>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '2rem' }}>
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                useOneTap
                shape="pill"
                theme="outline"
                text="signin_with"
                width="100%"
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', margin: '1.5rem 0', color: 'var(--google-grey-400)', fontSize: '0.75rem' }}>
              <div style={{ flex: 1, height: '1px', background: 'var(--google-grey-200)' }}></div>
              <span style={{ margin: '0 1rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>or</span>
              <div style={{ flex: 1, height: '1px', background: 'var(--google-grey-200)' }}></div>
            </div>
          </>
        )}

        {error && (
          <div className="error-message" style={{
            marginBottom: '1.5rem', 
            padding: '0.75rem', 
            background: '#fce8e6', 
            color: '#d93025', 
            borderRadius: '8px', 
            fontSize: '0.875rem',
            textAlign: 'left',
            border: '1px solid #f5c2c7'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {!is2FARequired ? (
            <>
              <div className="form-group">
                <label>Username</label>
                <input 
                  value={username} 
                  onChange={e => setUsername(e.target.value)} 
                  required 
                  autoFocus 
                  placeholder="Email or username"
                  style={{ borderRadius: '8px', padding: '0.875rem 1rem' }}
                />
              </div>
              <div className="form-group">
                <label>Password</label>
                <input 
                  type="password" 
                  value={password} 
                  onChange={e => setPassword(e.target.value)} 
                  required 
                  placeholder="Enter your password"
                  style={{ borderRadius: '8px', padding: '0.875rem 1rem' }}
                />
              </div>
            </>
          ) : (
            <>
              <div className="form-group">
                <label>2FA Verification Code</label>
                <input 
                  value={twoFactorCode} 
                  onChange={e => setTwoFactorCode(e.target.value)} 
                  placeholder="000000" 
                  autoFocus 
                  required 
                  style={{ 
                    borderRadius: '8px', 
                    padding: '1rem', 
                    fontSize: '1.5rem', 
                    textAlign: 'center', 
                    letterSpacing: '0.5rem',
                    fontWeight: 'bold'
                  }}
                />
              </div>
              <p style={{fontSize: '0.875rem', color: 'var(--google-grey-600)', marginBottom: '1.5rem', textAlign: 'left'}}>
                Enter the 6-digit code from your authenticator app to continue.
              </p>
            </>
          )}

          <div style={{display: 'flex', justifyContent: is2FARequired ? 'space-between' : 'flex-end', marginTop: '2.5rem', alignItems: 'center'}}>
            {is2FARequired && (
              <button 
                type="button" 
                className="secondary-btn" 
                onClick={() => { setIs2FARequired(false); setTwoFactorCode(''); }}
                style={{ border: 'none', background: 'transparent', color: 'var(--google-blue)', padding: '0.5rem' }}
              >
                Back
              </button>
            )}
            <button
              type="submit"
              className="submit-btn"
              style={{ minWidth: '100px', padding: '0.75rem 2rem' }}
              disabled={isSubmitting}
            >
              {isSubmitting
                ? (is2FARequired ? 'Verifying…' : 'Signing in…')
                : (is2FARequired ? 'Verify' : 'Next')}
            </button>
          </div>
        </form>

        <div style={{marginTop: '4rem', fontSize: '0.75rem', color: 'var(--google-grey-500)', display: 'flex', justifyContent: 'space-between'}}>
          <span>Farm Admin System</span>
          <span>v{version}</span>
        </div>
      </motion.div>
    </div>
  );
}

export default Login;
